import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import { FaArrowLeft, FaShieldAlt } from "react-icons/fa";
import Logo from "./Logo";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  usePageTitle("Privacy Policy");
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="relative z-10">
        {/* Header */}
        <motion.div
          className="container mx-auto px-4 py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-4 mb-8"
          >
            <motion.button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-romantic-pink hover:text-white transition-colors duration-300"
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaArrowLeft className="text-xl" />
              <span className="font-semibold">Back to Home</span>
            </motion.button>
          </motion.div>

          <motion.div variants={itemVariants} className="text-center mb-12">
            <div className="flex justify-center items-center gap-4 flex-col">
              <div className="text-center lg:text-left">
                <img
                  src="/assets/moon-logo.png"
                  alt="Logo"
                  className="w-32 mx-auto lg:mx-0"
                />
              </div>
              <div className="text-center lg:text-left">
                <img
                  src="/assets/png-logo.png"
                  alt="Logo"
                  className="w-40 mx-auto lg:mx-0 pb-2"
                />
              </div>
            </div>
            {/* <Logo size="large" className="mb-6 justify-center" /> */}
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Last Updated: December 1, 2025
            </p>
          </motion.div>
        </motion.div>

        {/* Content */}
        <motion.div
          className="container mx-auto px-4 pb-20"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            variants={itemVariants}
            className="glass rounded-3xl p-8 md:p-12 max-w-4xl mx-auto relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-romantic-purple to-romantic-pink opacity-5" />

            <div className="relative z-10 prose prose-invert prose-lg max-w-none">
              <div className="text-gray-300 leading-relaxed space-y-6">
                <h2 className="text-2xl font-bold text-romantic-pink">Introduction</h2>
                <p>
                  Welcome to Datenite, a dating application designed to help people match, chat, and meet in a safe and meaningful way. 
                </p>
                <p>
                  Your privacy matters to us as much as your connections do. This Privacy Policy explains what data we collect, why we collect it, how we use it, and how we protect it, in compliance with: 
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>UK GDPR</li>
                  <li>Data Protection Act 2018</li>
                  <li>Privacy and Electronic Communications Regulations (PECR)</li>
                </ul>
                <p>By using Datenite, you agree to the practices described in this policy.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">2. Who We Are</h2>
                <p><strong>Datenite</strong></p>
                <p>Website: <a href="https://datenite.co.uk" className="text-romantic-pink hover:underline">https://datenite.co.uk</a></p>
                <p>Email: <a href="mailto:support@datenite.co.uk" className="text-romantic-pink hover:underline">support@datenite.co.uk</a></p>
                <p>Data Controller: datenite</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">3. Information We Collect</h2>
                <p>We collect the following categories of personal data:</p>

                <h3 className="text-xl font-semibold text-white mt-6">A. Information You Provide Directly</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Phone number (optional)</li>
                  <li>Gender and preferences</li>
                  <li>Date of birth</li>
                  <li>Photos and media you upload</li>
                  <li>About Me / Bio</li>
                  <li>Interests</li>
                  <li>Personality questions or compatibility answers</li>
                  <li>Chat messages</li>
                  <li>Video call usage metadata (not recordings)</li>
                  <li>Date/event calendar submissions</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-6">B. Automatically Collected Data</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Device information (model, OS, device ID)</li>
                  <li>IP address</li>
                  <li>Approximate or precise location (with permission)</li>
                  <li>App interaction data</li>
                  <li>Match interactions (likes, dislikes, blocks, reports)</li>
                  <li>Session logs and crash reports</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-6">C. Sensitive Information</h3>
                <p>We may process sensitive personal data such as:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Sexual orientation</li>
                  <li>Dating preferences</li>
                  <li>Relationship choices</li>
                  <li>Profile photos</li>
                </ul>
                <p>We ONLY process this information when you provide it voluntarily.</p>

                <h3 className="text-xl font-semibold text-white mt-6">D. Payment & Subscription Data</h3>
                <p>Payments are processed securely by:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Apple App Store</li>
                  <li>Google Play Store</li>
                </ul>
                <p>We do not store full card details.</p>

                <h3 className="text-xl font-semibold text-white mt-6">E. Information from Third-Party Services</h3>
                <p>We use third-party SDKs including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Google Firebase (Analytics, Crashlytics, Messaging)</li>
                  <li>Google Maps / Geolocation</li>
                  <li>Meta Pixel (website only)</li>
                  <li>App Store & Play Store Billing APIs</li>
                  <li>Agora for Video calling</li>
                </ul>
                <p>These services may collect device, analytics, and usage data.</p>
                <p>All third-party tools comply with GDPR.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">4. How We Use Your Data</h2>
                <p>We process your data for the following lawful purposes:</p>

                <h3 className="text-xl font-semibold text-white mt-6">A. To Provide Dating App Services</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Creating your account</li>
                  <li>Showing compatible matches</li>
                  <li>Sending notifications</li>
                  <li>Chat functionality</li>
                  <li>Video calling</li>
                  <li>Calendar-based date publishing</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-6">B. To Improve App Performance</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Analytics</li>
                  <li>Crash reporting</li>
                  <li>Bug fixes</li>
                  <li>Feature optimisation</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-6">C. To Keep the Platform Safe</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Fraud detection</li>
                  <li>Filtering duplicate or fake accounts</li>
                  <li>Reviewing reports</li>
                  <li>Blocking harmful users</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-6">D. Marketing & Communications (With Consent)</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Promotional emails</li>
                  <li>App notifications</li>
                  <li>Feature announcements</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-6">E. Subscription Billing & Management</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Processing payments</li>
                  <li>Auto-renewal handling</li>
                </ul>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">5. Legal Basis for Processing</h2>
                <p>We rely on the following legal bases:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Contractual necessity</strong> – To provide account services</li>
                  <li><strong>Legitimate interests</strong> – To maintain safety & improve user experience</li>
                  <li><strong>Consent</strong> – For marketing, push notifications, and location access</li>
                  <li><strong>Legal obligation</strong> – For compliance and fraud prevention</li>
                </ul>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">6. How Your Data Is Stored & Protected</h2>
                <p>We store data securely using encrypted cloud infrastructure.</p>
                <p>Security measures include:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>End-to-end encryption for sensitive data</li>
                  <li>Encrypted database storage</li>
                  <li>Role-based data access control</li>
                  <li>Firewall and security monitoring</li>
                  <li>Regular audits and vulnerability checks</li>
                </ul>
                <p>We do not sell personal data to advertisers.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">7. How Long We Keep Data</h2>
                <p>We retain personal data only as long as necessary:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Active account data</strong> → until account is deleted</li>
                  <li><strong>Chat data</strong> → until user deletes or after account removal</li>
                  <li><strong>Logs & analytics</strong> → up to 24 months</li>
                  <li><strong>Legal compliance data</strong> → as required by UK law</li>
                </ul>
                <p>You may send Delete request from App itself anytime you want. Once the account is deleted.. all the Data, Subscription plan associated with your account will be null and Void. You cannot create new account with same login credentials.  </p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">8. Sharing Your Information</h2>
                <p>We share data only with:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Third-party processors (e.g., Firebase, cloud hosting, Agora)</li>
                  <li>Payment providers (Apple, Google)</li>
                  <li>Moderation & safety partners</li>
                  <li>Legal authorities (when required)</li>
                </ul>
                <p>No selling, renting, or sharing for profit.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">9. International Transfers</h2>
                <p>Some third-party tools (e.g., Google, Meta) may process data outside the UK.</p>
                <p>We ensure protection via:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Standard Contractual Clauses (SCCs)</li>
                  <li>Adequacy decisions</li>
                  <li>GDPR-compliant agreements</li>
                </ul>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">10. Child Safety Policy</h2>
                <p>Datenite is strictly for users aged 18 and above.</p>
                <p>We do not knowingly collect or store data from minors.</p>
                <p>If we discover an underage account:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>It is immediately removed</li>
                  <li>All data is deleted</li>
                </ul>
                <p>Parents may contact us to request removal of mistakenly created accounts.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">11. Your Rights Under UK GDPR</h2>
                <p>You have the right to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access your data</li>
                  <li>Correct inaccuracies</li>
                  <li>Delete your account</li>
                  <li>Restrict processing</li>
                  <li>Withdraw consent</li>
                  <li>Port your data</li>
                  <li>Object to processing</li>
                  <li>Lodge complaints with the ICO</li>
                </ul>
                <p>Request rights at <a href="mailto:support@datenite.co.uk" className="text-romantic-pink hover:underline">support@datenite.co.uk</a>.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">12. Auto-Renewal Disclosure</h2>
                <p>Subscriptions purchased through Apple App Store or Google Play Store:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Renew automatically unless cancelled</li>
                  <li>Can be cancelled anytime via Apple/Google account settings</li>
                  <li>Are billed by Apple/Google, not Datenite</li>
                  <li>Are subject to their refund policies</li>
                </ul>
                <p>We do not manage manual cancellations.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">13. Changes to This Privacy Policy</h2>
                <p>We may update this policy.</p>
                <p>Revised versions will be posted with an updated date.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">14. Contact Us</h2>
                <p>For privacy requests or concerns:</p>
                <p>📧 <a href="mailto:support@datenite.co.uk" className="text-romantic-pink hover:underline">support@datenite.co.uk</a></p>
                <p>🌐 <a href="https://datenite.co.uk" className="text-romantic-pink hover:underline">https://datenite.co.uk</a></p>
                
                <p className="mt-6">
                  <strong>Links to Other Websites</strong>
                  <br />
                  Our Platform may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review their privacy policies before providing any personal information.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
